import { get, isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "shared/components/Header";
import ProcessStepper from "shared/components/ProcessStepper";
import { NAME_SPACES } from "shared/locales/constants";
import {
  COLLECTIVE_OFFER_RANKS,
  CONTRIBUTION_STRUCTURES,
} from "utils/constants";
import { DEFAULT_COMMERCIAL_ADJUSTMENT, STEP_INDEXES } from "../constants";
import {
  combinationCompositionErrors,
  combinationsWithBase,
  combinationsWithUpsell,
  configuredCombinations,
  emptyCombination,
  headcountAllocationErrors,
  headcountsWithoutFollowing,
  isPricingReady,
  levelsByPlanId,
  membersOfStructure,
  persistedCombinations,
} from "../helpers";
import {
  useCollectiveCatalog,
  useCollectivePlanDocuments,
} from "../useCollectivePlans";
import useCollectiveCost from "../useCollectiveCost";
import useCoveragePlans from "../useCoveragePlans";
import View from "./View";

const defaultHeadcounts = (structure, headcount) => {
  const [primaryMember] = membersOfStructure(structure);
  if (!primaryMember) return {};
  return {
    [COLLECTIVE_OFFER_RANKS.BASE]: { [primaryMember]: Number(headcount) || 0 },
  };
};

const initialCombinations = (fields) => {
  const stored = configuredCombinations(get(fields, "combinations"));
  return isEmpty(stored)
    ? [emptyCombination(COLLECTIVE_OFFER_RANKS.BASE)]
    : stored;
};

const Offer = ({ project, processId, onNext, onBack }) => {
  const [nextLoading, setNextLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const TRANSLATION = t("DETAIL.TEMPLATES.ACORIS_COLLECTIVE", {
    returnObjects: true,
  });

  const [storedFields] = useState(() => get(project, "fields", {}));
  const [combinations, setCombinations] = useState(() =>
    initialCombinations(storedFields),
  );
  const [structure, setStructure] = useState(
    () =>
      get(storedFields, "contributionStructure") ||
      CONTRIBUTION_STRUCTURES.EMPLOYEE_SPOUSE_CHILD,
  );
  const [regime, setRegime] = useState(() => get(storedFields, "socialRegime"));
  const [commercialAdjustment, setCommercialAdjustment] = useState(
    () =>
      get(storedFields, "commercialAdjustment") ??
      DEFAULT_COMMERCIAL_ADJUSTMENT,
  );
  const [headcounts, setHeadcounts] = useState(
    () =>
      get(storedFields, "headcounts") ||
      defaultHeadcounts(structure, get(storedFields, "headcount")),
  );

  const { levelPlans, upsellPlans, offeredUpsellIds, loading } =
    useCollectiveCatalog(processId);
  const levels = useMemo(() => levelsByPlanId(levelPlans), [levelPlans]);

  const compositionErrors = useMemo(
    () => combinationCompositionErrors(combinations, levels),
    [combinations, levels],
  );

  const allocationErrors = useMemo(
    () =>
      headcountAllocationErrors({
        headcounts,
        structure,
        headcount: get(storedFields, "headcount"),
      }),
    [headcounts, structure, storedFields],
  );

  const draftFields = useMemo(() => {
    const configured = configuredCombinations(combinations);
    return {
      ...storedFields,
      combinations: configured,
      selectedPlan: get(configured, [0, "baseInsurancePlanId"], null),
      contributionStructure: structure,
      socialRegime: regime,
      commercialAdjustment,
      headcounts,
    };
  }, [
    storedFields,
    combinations,
    structure,
    regime,
    commercialAdjustment,
    headcounts,
  ]);

  const {
    cost,
    pending,
    loading: costLoading,
    refreshing,
    failed,
  } = useCollectiveCost({
    projectId: project.id,
    fields: draftFields,
    ready: isEmpty(compositionErrors) && isPricingReady(draftFields),
  });

  const coveragePlanIds = combinations.map(
    ({ baseInsurancePlanId }) => baseInsurancePlanId,
  );
  const { plans: coveragePlans, plansById: coveragePlansById } =
    useCoveragePlans(
      coveragePlanIds.some(Boolean)
        ? coveragePlanIds
        : [get(levelPlans, [0, "id"])],
    );

  const documentsPlan =
    levelPlans.find(({ id }) => id === draftFields.selectedPlan) ||
    get(levelPlans, 0);
  const documents = useCollectivePlanDocuments(documentsPlan, TRANSLATION);

  const onStructureChange = (nextStructure) => {
    setStructure(nextStructure);
    setHeadcounts(
      defaultHeadcounts(nextStructure, get(storedFields, "headcount")),
    );
  };

  const onHeadcountChange = (rank, member, count) =>
    setHeadcounts((previous) => ({
      ...previous,
      [rank]: { ...get(previous, rank, {}), [member]: count },
    }));

  const onPlanChange = (rank, planId) => {
    setCombinations((previous) =>
      combinationsWithBase(previous, rank, planId, offeredUpsellIds),
    );
    if (!planId)
      setHeadcounts((previous) => headcountsWithoutFollowing(previous, rank));
  };

  const onOptionAdd = (rank) =>
    setCombinations((previous) => [...previous, emptyCombination(rank)]);

  const onOptionRemove = (rank) => {
    setCombinations((previous) =>
      previous.slice(
        0,
        previous.findIndex(({ id }) => id === rank),
      ),
    );
    setHeadcounts((previous) => headcountsWithoutFollowing(previous, rank));
  };

  const onUpsellToggle = (rank, upsellId, checked) =>
    setCombinations((previous) =>
      combinationsWithUpsell(previous, rank, upsellId, checked),
    );

  const onSubmit = () => {
    setNextLoading(true);
    onNext({
      payload: {
        fields: {
          ...get(project, "fields", {}),
          ...draftFields,
          combinations: persistedCombinations(
            draftFields.combinations,
            get(project, "fields.combinations"),
          ),
        },
      },
      onCompleted: () => setNextLoading(false),
    });
  };

  const steps = useMemo(
    () => Object.values(TRANSLATION.STEPS).map((title) => ({ title })),
    [TRANSLATION],
  );

  return (
    <>
      <Header
        key="header"
        title={TRANSLATION.OFFER.TITLE}
        subtitle={TRANSLATION.OFFER.SUBTITLE}
        actions={[]}
      />
      <ProcessStepper
        steps={steps}
        current={STEP_INDEXES.OFFER}
        aria-label={TRANSLATION.STEPPER_LABEL}
      />
      <View
        translation={TRANSLATION}
        levelPlans={levelPlans}
        upsellPlans={upsellPlans}
        offeredUpsellIds={offeredUpsellIds}
        catalogLoading={loading}
        combinations={combinations}
        coveragePlans={coveragePlans}
        coveragePlansById={coveragePlansById}
        structure={structure}
        regime={regime}
        headcounts={headcounts}
        allocationErrors={allocationErrors}
        commercialAdjustment={commercialAdjustment}
        compositionErrors={compositionErrors}
        cost={cost}
        costPending={pending}
        costLoading={costLoading}
        costRefreshing={refreshing}
        costFailed={failed || (!loading && isEmpty(levelPlans))}
        onPlanChange={onPlanChange}
        onUpsellToggle={onUpsellToggle}
        onOptionAdd={onOptionAdd}
        onOptionRemove={onOptionRemove}
        onStructureChange={onStructureChange}
        onRegimeChange={setRegime}
        onHeadcountChange={onHeadcountChange}
        onCommercialAdjustmentChange={setCommercialAdjustment}
        onBack={onBack}
        onSubmit={onSubmit}
        nextLoading={nextLoading}
        disabled={get(project, "expired") || get(project, "locked")}
        documents={documents}
      />
    </>
  );
};

export default Offer;
